import { useLocale, useTranslations } from "next-intl";
import Image from "next/image";
import React, { ChangeEvent, ComponentPropsWithRef, forwardRef, useEffect, useId, useMemo, useRef, useState } from "react";
import { ControllerFieldState, ControllerRenderProps } from "react-hook-form";

import Label from "@/components/ui/Form/Label/Label";
import { LabelValue } from "@/types";
import cn from "@/utils/cn";

import Icon from "../../Icon/Icon";

export interface SelectOptions extends LabelValue {
  selected?: boolean;
  disabled?: boolean;
}

export interface SelectProps extends ComponentPropsWithRef<"select"> {
  name: string;
  reverse?: boolean;
  hideBorder?: boolean;
  hideLabel?: boolean;
  label: string;
  options: SelectOptions[];
  field?: ControllerRenderProps;
  fieldState?: ControllerFieldState;
  selectStyle?: any;
  selectClassName?: string;
  searchable?: boolean;
  positionSearchable?: "absolute" | "fixed";
}

const SelectBase = forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      name,
      reverse,
      label,
      options,
      hideLabel,
      field,
      fieldState,
      selectStyle,
      selectClassName,
      hideBorder,
      searchable,
      positionSearchable,
      ...rest
    },
    ref,
  ) => {
    const id = useId();
    const [searchTerm, setSearchTerm] = useState("");
    const [isOpen, setIsOpen] = useState(false);
    const [dropdownPosition, setDropdownPosition] = useState<"bottom" | "top">("bottom");
    const dropdownRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLDivElement>(null);
    const locale = useLocale();
    const t = useTranslations();
    const isError = !!fieldState?.error;
    const errorMessage = fieldState?.error?.message ? t(fieldState.error.message) : "";

    const defaultID: string = useMemo(() => {
      return rest?.id ?? id;
    }, [id, rest?.id]);

    const filteredOptions = useMemo(() => {
      if (!searchable) return options;
      return options.filter((option) => option.label?.toString().toLowerCase().includes(searchTerm.toLowerCase()));
    }, [options, searchTerm, searchable]);

    const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(e.target.value);
    };

    const handleOptionClick = (e: any) => {
      console.log("E ===>", e);

      rest?.onChange?.(e);
      field?.onChange(e);
      setIsOpen(false);
    };

    // Calculate dropdown position based on available space
    const calculateDropdownPosition = () => {
      if (triggerRef.current && dropdownRef.current) {
        const triggerRect = triggerRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - triggerRect.bottom;
        const spaceAbove = triggerRect.top;
        const dropdownHeight = 200; // Approximate height of the dropdown

        if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
          setDropdownPosition("top");
        } else {
          setDropdownPosition("bottom");
        }
      }
    };

    // Open dropdown and calculate position
    const handleToggleDropdown = () => {
      if (!isOpen) {
        calculateDropdownPosition();
      }
      setIsOpen(!isOpen);
    };

    // Close dropdown when clicking outside
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
          setIsOpen(false);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, []);

    return (
      <div
        className={cn(
          "select-wrapper",
          reverse && "select-wrapper-reverse",
          hideBorder ? "no-border" : "",
          rest.disabled && "text-gray-30",
          rest.className,
        )}
      >
        <div className={`block h-m pt-xs md:pt-0 ${locale?.split("-")[0] === "ar" ? "pr-[8px]" : ""}`}>
          <Label htmlFor={defaultID} reverse={reverse} required={rest?.required ?? false}>
            {label}
          </Label>
        </div>
        {searchable ? (
          <div className="relative" ref={dropdownRef}>
            <div
              ref={triggerRef}
              className={cn("selected-value p-2 border-b cursor-pointer", isError && "border-b-[#B42318]")}
              onClick={handleToggleDropdown}
              style={{ ...selectStyle, ...(locale?.split("-")[0] === "ar" ? { direction: "ltr" } : {}) }}
            >
              {field?.value?.split("_")[0] || rest?.value || ""}
            </div>

            {isOpen && (
              <div
                className={cn(
                  `searchable-dropdown ${positionSearchable}  w-[250px] h-[200px] bg-white border rounded shadow-lg z-10 absolute`,
                  dropdownPosition === "bottom" ? "mt-1" : "bottom-full mb-1",
                )}
              >
                <input
                  type="text"
                  placeholder={t("label.countrydropdown.placeholder")}
                  value={searchTerm}
                  onChange={handleSearchChange}
                  onKeyDown={(e) => {
                    // Prevent Enter from triggering native (uncontrolled) form
                    // submission - this input sits inside a <form>, and without
                    // preventDefault() the browser performs implicit submission,
                    // bypassing React's action handling. stopPropagation keeps it
                    // from also reaching the form-level Enter-to-submit guard below.
                    if (e.key === "Enter") {
                      e.preventDefault();
                      e.stopPropagation();
                      const firstMatch = filteredOptions.find((option) => !option.disabled);
                      if (firstMatch) {
                        handleOptionClick({ target: { value: firstMatch.value }, currentTarget: { value: firstMatch.value } });
                      }
                    }
                  }}
                  className="w-full p-2 border-b focus:outline-none"
                />
                <ul
                  className="h-[80%] w-full overflow-y-scroll"
                  style={{
                    ...(locale?.split("-")[0] === "ar" ? { direction: "ltr" } : {}),
                  }}
                >
                  {filteredOptions.map((option, index) => {
                    return (
                      <li
                        key={`${option.value}-${index}`}
                        className={cn("p-2 hover:bg-gray-100 cursor-pointer", option.disabled && "text-gray-400 cursor-not-allowed")}
                        onClick={() =>
                          !option.disabled && handleOptionClick({ target: { value: option.value }, currentTarget: { value: option.value } })
                        }
                        style={{
                          backgroundImage: `url('/icons/${option.country.toLowerCase()}.png')`,
                          backgroundSize: "20px, 20px",
                          backgroundPosition: "left center, 4px center",
                          backgroundRepeat: "no-repeat",
                          paddingLeft: "1.5rem",
                          marginLeft: "0.1rem",
                        }}
                      >
                        {option.label}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
        ) : (
          <select
            {...rest}
            {...field}
            onChange={(e: ChangeEvent<HTMLSelectElement>) => {
              rest?.onChange?.(e);
              field?.onChange(e);
            }}
            ref={ref}
            name={name}
            id={defaultID}
            className={cn(selectClassName, isError && "border-b-[#B42318]")}
            style={{ ...selectStyle }}
            value={field?.value || rest?.value || ""}
          >
            {options.map((option, index) => (
              <option key={`${option.value}-${index}`} value={option.value} disabled={option.disabled}>
                {option.label}
              </option>
            ))}
          </select>
        )}

        <span
          className={`block h-m text-xs leading-[7px] font-secondary pt-xs px-xxs ${isError ? "text-[#B42318]" : reverse ? "text-gray-10" : "text-gray-70"}`}
        >
          {isError ? errorMessage : ""}
        </span>
      </div>
    );
  },
);
SelectBase.displayName = "SelectBase";
export default SelectBase;
